import { createContext, useContext, useRef, useState, ReactNode, SyntheticEvent } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

type Severity = 'success' | 'error' | 'warning' | 'info';

type AlertOptions = {
    timeout?: number;
};

interface AlertContextValue {
    open: boolean;
    openAlert: () => void;
    closeAlert: (_event?: SyntheticEvent | Event, _reason?: any, id?: number) => void;
    setSuccessMessage: (message: string, options?: AlertOptions) => void;
    setErrorMessage: (message: string, options?: AlertOptions) => void;
    setNeutralMessage: (message: string, options?: AlertOptions) => void;
}

const AlertContext = createContext<AlertContextValue>({
    open: false,
    openAlert: () => {},
    closeAlert: (_event?: SyntheticEvent | Event) => {},
    setSuccessMessage: (_message: string, _options?: AlertOptions) => {},
    setErrorMessage: (_message: string, _options?: AlertOptions) => {},
    setNeutralMessage: (_message: string, _options?: AlertOptions) => {},
});

export const useAlert = () => {
    return useContext(AlertContext);
};

export const AlertProvider = ({ children }: { children: ReactNode }) => {
    const [alerts, setAlerts] = useState<
        Array<{ id: number; severity: Severity; message: string }>
    >([]);
    const idRef = useRef(0);

    const openAlert = () => {
        // kept for backward-compatibility; no-op since we push via set*Message helpers
    };

    const removeById = (id: number) => {
        setAlerts(prev => prev.filter(a => a.id !== id));
    };

    const closeAlert = (_event?: SyntheticEvent | Event, _reason?: any, id?: number) => {
        if (typeof id === 'number') {
            removeById(id);
        } else if (alerts.length > 0) {
            setAlerts(prev => prev.slice(1));
        }
    };

    const push = (severity: Severity, message: string, options?: AlertOptions) => {
        const id = ++idRef.current;
        setAlerts(prev => [...prev, { id, severity, message }]);
        const timeout = options?.timeout ?? 3000;
        if (typeof timeout === 'number' && timeout > 0) {
            window.setTimeout(() => removeById(id), timeout);
        }
    };

    const setSuccessMessage = (message: string, options?: AlertOptions) => {
        push('success', message, options);
    };

    const setErrorMessage = (message: string, options?: AlertOptions) => {
        push('error', message, options);
    };

    const setNeutralMessage = (message: string, options?: AlertOptions) => {
        push('info', message, options);
    };

    return (
        <AlertContext.Provider
            value={{
                open: alerts.length > 0,
                openAlert,
                closeAlert,
                setSuccessMessage,
                setErrorMessage,
                setNeutralMessage,
            }}
        >
            {children}
            <Box
                sx={{
                    position: 'fixed',
                    top: 16,
                    left: 0,
                    right: 0,
                    zIndex: theme => theme.zIndex.snackbar,
                    display: 'flex',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                }}
            >
                <Stack spacing={1} sx={{ width: 'min(400px, 90vw)', pointerEvents: 'auto' }}>
                    {alerts.map(a => (
                        <Alert
                            key={a.id}
                            severity={a.severity}
                            onClose={event => closeAlert(event, undefined, a.id)}
                        >
                            {a.message}
                        </Alert>
                    ))}
                </Stack>
            </Box>
        </AlertContext.Provider>
    );
};
